import { useState } from "react";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { KeyRound, LogOut, Trash2, Globe, Plus, ChevronDown, Users } from "lucide-react";
import toast from "react-hot-toast";
import { useAuth } from "../hooks/useAuth";
import { listRegisteredOAuthClients, removeRegisteredOAuthClient, listTenantLogins } from "../lib/sailpoint";
import { SkeletonList, EmptyState, ErrorBox, ConfirmModal, Spinner } from "../components/ui";
import { RegisterTenantForm } from "../components/RegisterTenantForm";

/**
 * Expanded under a tenant row: everyone who has signed into that tenant
 * through this server, with how many times and when last. Server-side data
 * only — no SailPoint calls, so it works even when the tenant itself is
 * unreachable.
 */
function TenantLoginList({ tenant }) {
  const { data: users, isLoading, error } = useQuery({
    queryKey: ["tenant-logins", tenant],
    queryFn: () => listTenantLogins(tenant),
  });

  if (isLoading) {
    return (
      <div className="flex items-center justify-center py-4">
        <Spinner size={16} />
      </div>
    );
  }
  if (error) {
    return (
      <p className="px-5 py-3 text-xs text-red-600">
        {error.response?.data?.error || error.message}
      </p>
    );
  }
  if (!users?.length) {
    return <p className="px-5 py-3 text-xs text-gray-500">No one has signed into this tenant yet.</p>;
  }
  return (
    <div>
      {users.map((u) => (
        <div key={u.username} className="flex items-center gap-3 px-5 py-2.5 border-t border-gray-100">
          <div className="w-7 h-7 rounded-full bg-gray-100 flex items-center justify-center text-gray-600 text-[11px] font-bold flex-shrink-0">
            {(u.displayName || u.username).charAt(0).toUpperCase()}
          </div>
          <div className="flex-1 min-w-0">
            <p className="text-xs font-medium text-gray-900 truncate">{u.displayName}</p>
            <p className="text-[11px] text-gray-500 truncate">
              {u.username}
              {u.lastLoginAt && (
                <span className="text-gray-400"> · last {new Date(u.lastLoginAt).toLocaleString()}</span>
              )}
            </p>
          </div>
          <span className="text-[11px] font-semibold text-blue-700 bg-blue-50 rounded-full px-2 py-0.5 flex-shrink-0">
            {u.count} login{u.count === 1 ? "" : "s"}
          </span>
        </div>
      ))}
    </div>
  );
}

/**
 * The entire UI a local admin session gets: the stored OAuth client registry,
 * with remove. No tenant data, no SailPoint calls, and nothing secret on
 * screen — the server never returns client secrets or service credentials to
 * this page in the first place.
 */
export default function LocalAdminPage() {
  const { session, logout } = useAuth();
  const queryClient = useQueryClient();
  const [removing, setRemoving] = useState(null); // client row pending confirm
  const [expanded, setExpanded] = useState(null); // tenant whose login list is open

  const { data: clients, isLoading, error, refetch } = useQuery({
    queryKey: ["local-admin-oauth-clients"],
    queryFn: listRegisteredOAuthClients,
  });

  const [showRegister, setShowRegister] = useState(false);

  const removeMutation = useMutation({
    mutationFn: (tenant) => removeRegisteredOAuthClient(tenant),
    onSuccess: (_data, tenant) => {
      toast.success(`Removed the OAuth client registration for ${tenant}`);
      setRemoving(null);
      queryClient.invalidateQueries({ queryKey: ["local-admin-oauth-clients"] });
    },
    onError: (err) => {
      toast.error(err.response?.data?.error || err.message);
      setRemoving(null);
    },
  });

  return (
    <div className="min-h-screen bg-gray-50 flex flex-col items-center px-4 py-10">
      <div className="w-full max-w-lg">
        <div className="flex items-center justify-between mb-6">
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 bg-amber-100 rounded-xl flex items-center justify-center">
              <KeyRound size={20} className="text-amber-600" />
            </div>
            <div>
              <h1 className="text-lg font-bold text-gray-900">Local Admin</h1>
              <p className="text-xs text-gray-500">
                Signed in as {session?.identity?.username || "admin"}
              </p>
            </div>
          </div>
          <button
            onClick={logout}
            className="flex items-center gap-1.5 text-sm font-medium text-gray-500 hover:text-gray-700 px-3 py-2"
          >
            <LogOut size={15} />
            Sign out
          </button>
        </div>

        <div className="bg-white rounded-2xl border border-gray-200 shadow-sm overflow-hidden">
          <div className="px-5 py-4 border-b border-gray-100">
            <h2 className="text-sm font-semibold text-gray-900">Registered OAuth clients</h2>
            <p className="text-xs text-gray-500 mt-0.5">
              Removing an entry only un-registers it from this server — the
              client inside SailPoint is untouched. The tenant can be set up
              again from the sign-in screen.
            </p>
          </div>

          {error && <ErrorBox message={error.response?.data?.error || error.message} onRetry={refetch} />}
          {isLoading && <SkeletonList rows={3} />}

          {!isLoading && !error && (clients || []).length === 0 && (
            <EmptyState
              icon={Globe}
              title="No OAuth clients registered"
              subtitle="No tenants are set up for sign-in on this server"
            />
          )}

          {!isLoading && !error &&
            (clients || []).map((c) => (
              <div key={c.tenant} className="border-b border-gray-100 last:border-0">
                {/* The row itself toggles the tenant's sign-in list below it. */}
                <div
                  role="button"
                  tabIndex={0}
                  onClick={() => setExpanded((t) => (t === c.tenant ? null : c.tenant))}
                  onKeyDown={(e) => e.key === "Enter" && setExpanded((t) => (t === c.tenant ? null : c.tenant))}
                  className="flex items-center gap-3 px-5 py-3.5 cursor-pointer hover:bg-gray-50"
                >
                  <div className="w-9 h-9 rounded-full bg-blue-50 flex items-center justify-center flex-shrink-0">
                    <Globe size={15} className="text-blue-600" />
                  </div>
                  <div className="flex-1 min-w-0">
                    <p className="text-sm font-medium text-gray-900 truncate">{c.siteName}</p>
                    <p className="text-xs text-gray-500 truncate">
                      {c.tenant}
                      {c.clientId && <span className="text-gray-400"> · client {c.clientId}</span>}
                    </p>
                  </div>
                  <button
                    onClick={(e) => {
                      e.stopPropagation();
                      setRemoving(c);
                    }}
                    className="flex items-center gap-1.5 text-xs font-medium text-red-600 hover:text-red-700 flex-shrink-0 px-2 py-1"
                  >
                    <Trash2 size={14} />
                    Remove
                  </button>
                  <ChevronDown
                    size={15}
                    className={`text-gray-400 flex-shrink-0 transition-transform ${expanded === c.tenant ? "rotate-180" : ""}`}
                  />
                </div>
                {expanded === c.tenant && (
                  // Only classes the dark-theme override block in index.css
                  // remaps — an opacity variant like bg-gray-50/60 is a
                  // different class name, so it stays light in dark mode.
                  <div className="bg-gray-50 pb-1">
                    <div className="flex items-center gap-1.5 px-5 pt-2 pb-1">
                      <Users size={12} className="text-gray-400" />
                      <p className="text-[11px] font-semibold text-gray-500 uppercase tracking-wide">Sign-ins</p>
                    </div>
                    <TenantLoginList tenant={c.tenant} />
                  </div>
                )}
              </div>
            ))}
        </div>

        <div className="bg-white rounded-2xl border border-gray-200 shadow-sm overflow-hidden mt-6">
          <button
            onClick={() => setShowRegister((v) => !v)}
            className="w-full flex items-center justify-between px-5 py-4 text-left"
          >
            <div>
              <h2 className="text-sm font-semibold text-gray-900">Register a tenant</h2>
              <p className="text-xs text-gray-500 mt-0.5">
                Create (or replace) a tenant's sign-in client using an admin
                Personal Access Token — used once, never stored.
              </p>
            </div>
            <Plus size={16} className={`text-gray-400 transition-transform ${showRegister ? "rotate-45" : ""}`} />
          </button>
          {showRegister && (
            <div className="border-t border-gray-100">
              <RegisterTenantForm
                onDone={(t) => {
                  toast.success(`"${t}" is registered — sign-in is ready.`);
                  setShowRegister(false);
                  queryClient.invalidateQueries({ queryKey: ["local-admin-oauth-clients"] });
                }}
              />
            </div>
          )}
        </div>
      </div>

      {removing && (
        <ConfirmModal
          title="Remove OAuth client?"
          message={`Sign-in for "${removing.siteName}" (${removing.tenant}) will stop working on this server until the tenant is set up again. The client in SailPoint itself is not deleted.`}
          confirmLabel="Remove"
          danger
          pending={removeMutation.isPending}
          onConfirm={() => removeMutation.mutate(removing.tenant)}
          onCancel={() => setRemoving(null)}
        />
      )}
    </div>
  );
}
